import dayjs, { Dayjs } from 'dayjs';
import { TrafficLogVisitorType } from 'common/constans/common.constants';
import { ICitationRead } from 'common/services/citation-service/citation.service.types';
import { CitationStatus, RestrictionStateStatus } from 'features/eiq-manage/enums/citation-status.enum';

const DEFAULT_REPORT_PERIOD_YEARS = 1;
const ISO_DATE_PATTERN = /\d{4}-\d{2}-\d{2}/g;

export interface CitationActionAvailability {
  isConfirmDisabled: boolean;
  isApproveDisabled: boolean;
  isConfirmPaymentDisabled: boolean;
  isCancelDisabled: boolean;
  isEditDisabled: boolean;
  isMoveDisabled: boolean;
  isSuspendTransponderDisabled: boolean;
  isUnsaspendTransponderDisabled: boolean;
}

export interface CitationReportDateRange {
  dateFrom: Dayjs;
  dateTo: Dayjs;
}

export const getCitationActionAvailability = (citation?: ICitationRead | null): CitationActionAvailability => {
  const hasCitation = Boolean(citation);
  const hasResident = Boolean(citation?.residentId);
  const status = citation?.status;
  const isPending = status === CitationStatus.Pending;
  const isConfirmed = status === CitationStatus.Confirmed;
  const isFinalStatus =
    status === CitationStatus.Canceled ||
    status === CitationStatus.Released ||
    status === CitationStatus.Paid ||
    status === CitationStatus.Waived;

  return {
    isSuspendTransponderDisabled: !hasResident || isFinalStatus || isPending || isConfirmed,
    isUnsaspendTransponderDisabled: !hasResident || isPending || isConfirmed,
    isConfirmDisabled: !hasResident || !isPending,
    isApproveDisabled: !hasResident || status !== CitationStatus.Confirmed,
    isConfirmPaymentDisabled: !hasResident || (status !== CitationStatus.PayNow && status !== CitationStatus.Delinquent),
    isCancelDisabled: !hasResident || isFinalStatus,
    isEditDisabled: !hasCitation || !isPending,
    isMoveDisabled: !hasCitation || !isPending || (!citation?.guestName && !citation?.companyName),
  };
};

export const hasActivePenalty = (status?: CitationStatus | string | null): boolean => {
  return status === CitationStatus.PayNow || status === CitationStatus.PastDue || status === CitationStatus.Delinquent;
};

export const getCitationReportDateRange = (filters?: string | null): CitationReportDateRange => {
  const defaultRange = {
    dateFrom: dayjs().subtract(DEFAULT_REPORT_PERIOD_YEARS, 'year'),
    dateTo: dayjs(),
  };

  if (!filters) {
    return defaultRange;
  }

  const dates = filters.match(ISO_DATE_PATTERN);
  if (!dates || dates.length < 2) {
    return defaultRange;
  }

  const dateFrom = dayjs(dates[0]);
  const dateTo = dayjs(dates[1]);

  if (!dateFrom.isValid() || !dateTo.isValid()) {
    return defaultRange;
  }

  return { dateFrom, dateTo };
};

export const formatSpeed = (speed?: number | string | null): string => {
  return speed === null || speed === undefined || speed === '' ? 'N/A' : `${speed} mph`;
};

export const formatCurrency = (amount?: number | null): string => {
  return amount === null || amount === undefined ? 'N/A' : `$${amount}`;
};

export const formatOptionalNumber = (value?: number | null): string | number => {
  return value === null || value === undefined ? 'N/A' : value;
};

export const hasCoordinates = (latitude?: number | null, longitude?: number | null): boolean => {
  return latitude !== null && latitude !== undefined && longitude !== null && longitude !== undefined;
};

export const getGoogleMapsUrl = (latitude?: number | null, longitude?: number | null): string | null => {
  if (!hasCoordinates(latitude, longitude)) {
    return null;
  }

  return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

export const getStaticMapUrl = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  apiKey: string,
): string | null => {
  if (!apiKey || !hasCoordinates(latitude, longitude)) {
    return null;
  }

  return `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=16&size=400x400&markers=color:red|label:S|${latitude},${longitude}&key=${apiKey}`;
};

export const isGuestCitation = (citation: Pick<ICitationRead, 'ownerType'>): boolean => {
  return citation.ownerType !== TrafficLogVisitorType.Resident;
};

export const getGuestDisplayName = (citation: Pick<ICitationRead, 'guestName' | 'companyName'>): string => {
  if (citation.guestName && citation.companyName) {
    return `${citation.guestName} (${citation.companyName})`;
  }

  return citation.guestName || citation.companyName || '';
};

export const getRegisteredTo = (citation: Pick<ICitationRead, 'ownerType' | 'residentName' | 'guestName' | 'companyName'>): string => {
  return isGuestCitation(citation) ? getGuestDisplayName(citation) : citation.residentName || '';
};

export const getReportRegisteredTo = (citation: Pick<ICitationRead, 'residentName' | 'guestName' | 'companyName'>): string => {
  if (!citation.residentName) {
    return '';
  }

  const guest = citation.guestName ? ` - ${citation.guestName}` : '';
  const company = citation.companyName ? ` (${citation.companyName})` : '';

  return `${citation.residentName}${guest}${company}`;
};

export const getCitationConsequencesText = (citation: Pick<ICitationRead, 'totalPaymentAmount' | 'suspensionDays' | 'status'>): string => {
  if (citation.status === CitationStatus.Warning) {
    return 'Warning only';
  }

  const hasFine = citation.totalPaymentAmount !== null && citation.totalPaymentAmount !== undefined;
  const hasSuspension = citation.suspensionDays !== null && citation.suspensionDays !== undefined;

  if (hasFine && hasSuspension) {
    return `$${citation.totalPaymentAmount} fine with ${citation.suspensionDays} days suspension`;
  }

  if (hasFine) {
    return `$${citation.totalPaymentAmount} fine`;
  }

  if (hasSuspension) {
    return `${citation.suspensionDays} days suspension`;
  }

  return 'N/A';
};
